import { useState } from "react";
import { Check, ChevronDown, Package } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";

type Service = { id: string; name: string; especialidade?: { label: string } | null };

type Props = {
  selectedIds: string[];
  onToggle: (id: string) => void;
  onSelectAll: (ids: string[]) => void;
  onClear: () => void;
};

/** Lista inline — usada quando a sidebar da agenda está aberta/fixada. */
export function ServiceCheckboxList({ selectedIds, onToggle, onSelectAll, onClear }: Props) {
  const query = trpc.service.list.useQuery({ status: "ACTIVE" }, { staleTime: 60_000 });
  const services = (query.data?.services ?? []) as Service[];

  if (query.isLoading) {
    return (
      <div className="flex items-center gap-2 px-0.5 py-1 text-xs italic text-zinc-400 dark:text-muted-foreground">
        <Package className="h-3.5 w-3.5" />
        Carregando...
      </div>
    );
  }

  if (services.length === 0) {
    return (
      <div className="flex items-center gap-2 px-0.5 py-1 text-xs italic text-zinc-500 dark:text-muted-foreground">
        <Package className="h-3.5 w-3.5" />
        Nenhum procedimento cadastrado
      </div>
    );
  }

  const allSelected = services.every((s) => selectedIds.includes(s.id));

  return (
    <div className="space-y-0.5">
      {services.map((s) => {
        const checked = selectedIds.includes(s.id);
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => onToggle(s.id)}
            className={cn(
              "group flex w-full items-center gap-2 rounded-md px-1.5 py-1.5 text-left text-[12px] transition-colors",
              checked ? "bg-zinc-50 dark:bg-muted/30" : "hover:bg-zinc-50 dark:hover:bg-muted/30",
            )}
          >
            <Checkbox checked={checked} />
            <span className="min-w-0 flex-1 truncate">
              <span
                className={cn(
                  "block truncate",
                  checked ? "font-semibold text-zinc-900 dark:text-foreground" : "text-zinc-600 dark:text-muted-foreground",
                )}
              >
                {s.name}
              </span>
              <span className="block truncate text-[10px] text-zinc-400 dark:text-muted-foreground/70">
                {s.especialidade?.label}
              </span>
            </span>
          </button>
        );
      })}

      {services.length > 1 && (
        <div className="mt-1 flex gap-1 px-0.5 pt-1">
          <button
            type="button"
            onClick={() => (allSelected ? onClear() : onSelectAll(services.map((s) => s.id)))}
            className="flex-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium text-brand-700 transition-colors hover:bg-brand-50 dark:text-primary dark:hover:bg-primary/10"
          >
            {allSelected ? "Desmarcar todos" : "Selecionar todos"}
          </button>
        </div>
      )}
    </div>
  );
}

/** Botão dropdown — usado quando a sidebar está oculta (topbar). */
export function ServiceDropdownButton({ selectedIds, onToggle, onSelectAll, onClear }: Props) {
  const [open, setOpen] = useState(false);
  const query = trpc.service.list.useQuery({ status: "ACTIVE" }, { staleTime: 60_000 });
  const services = (query.data?.services ?? []) as Service[];
  const total = services.length;
  const selected = services.filter((s) => selectedIds.includes(s.id));
  const allSelected = total > 0 && selected.length === total;
  const none = selected.length === 0;

  const buttonLabel = none
    ? "Todos os procedimentos"
    : allSelected
      ? "Todos"
      : selected.length === 1
        ? selected[0].name
        : `${selected.length} procedimentos`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Filtrar procedimentos"
          className="inline-flex h-9 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-2.5 text-xs font-medium text-zinc-700 transition-colors hover:border-zinc-300 hover:bg-zinc-50 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-muted"
        >
          <Package className="h-3.5 w-3.5" />
          {/* Só mostra o texto em telas bem largas — no topbar da Agenda o espaço já é
              apertado com o filtro de dentista + ações; ícone+chevron bastam no resto. */}
          <span className="hidden 2xl:inline">{buttonLabel}</span>
          <ChevronDown className="h-3 w-3 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-64 border border-zinc-200 bg-white p-2 shadow-xl dark:border-border dark:bg-card"
      >
        <div className="mb-2 flex items-center justify-between px-1">
          <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
            Procedimentos
          </p>
          <span className="text-[10px] text-zinc-400 dark:text-muted-foreground/70">
            {selected.length}/{total}
          </span>
        </div>

        <ServiceCheckboxList
          selectedIds={selectedIds}
          onToggle={onToggle}
          onSelectAll={onSelectAll}
          onClear={onClear}
        />
      </PopoverContent>
    </Popover>
  );
}

function Checkbox({ checked }: { checked: boolean }) {
  return (
    <span
      className={cn(
        "flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border transition-all",
        checked
          ? "border-transparent bg-brand-600 dark:bg-primary"
          : "border-zinc-300 bg-white dark:border-border dark:bg-background",
      )}
    >
      {checked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
    </span>
  );
}
